// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import validator from "validator";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { OutstandingLoanModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/OutstandingLoanModel";
import { OutstandingLoanDTO } from "Globals/Models/DTO/OutstandingLoanDTO";

// ViewModels
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";

export default class OutstandingLoanViewModel extends ViewModelBase<OutstandingLoanModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(outstandingLoanModel?: OutstandingLoanModel) {
        super(outstandingLoanModel ? outstandingLoanModel : new OutstandingLoanModel());
    }

    @action
    public stash = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        let valid = await this.isModelValid();

        if (valid) {
            let model = new OutstandingLoanModel();
            model.fromDto(this.model);
            let viewModel = new OutstandingLoanViewModel(model);

            runInAction(() => {
                incomeDetails.outstandingLoanViewModels.push(viewModel);
                // triggers the auto-save.
                incomeDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (incomeDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.incomeDetailId = incomeDetailId;

            let dto: OutstandingLoanDTO = this.model.toDto();
            const apiResult = await this.Post<OutstandingLoanDTO>(AppUrls.Server.ApplicationForm.Upsert.OutstandingLoan, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("OutstandingLoan:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("OutstandingLoan:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("incomeDetailId")) {
                runInAction(() => {
                    let index = incomeDetails.outstandingLoanViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        incomeDetails.outstandingLoanViewModels.splice(index, 1);
                    }
                });
            } else {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.incomeDetailId = incomeDetails.model.id ? incomeDetails.model.id : "";
                this.model.isDeleted = true;

                let upsertOutstandingLoan: OutstandingLoanDTO = this.model.toDto();
                const apiResult = await this.Post<OutstandingLoanDTO>(AppUrls.Server.ApplicationForm.Upsert.OutstandingLoan, upsertOutstandingLoan);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("OutstandingLoan:Upsert-Successful");
                    let model = new OutstandingLoanModel();
                    model.fromDto(apiResult.payload);

                    runInAction(() => {
                        let index = incomeDetails.outstandingLoanViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                        if (index > -1) {
                            incomeDetails.outstandingLoanViewModels.splice(index, 1);
                        }
                    });
                    this.model.resetModel();
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("OutstandingLoan:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<OutstandingLoanModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<OutstandingLoanModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public setDate(fieldName: keyof FieldType<OutstandingLoanModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
    }

    @computed
    public get getFormattedFinalPaymentDate(): string {
        return moment.utc(this.model.dateOfFinalPayment).local().format("DD/MM/YYYY");
    }

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<OutstandingLoanModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<OutstandingLoanModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<OutstandingLoanModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "loanPurposeId") {
            if (value && !validator.isEmpty(value)) {
                if (value == "0") {
                    errorMessage = "Loan purpose is required";
                    isValid = false;
                }
            }
        }
        if (fieldName == "lenderId") {
            if (value && !validator.isEmpty(value)) {
                if (value == "0") {
                    errorMessage = "Lender is required";
                    isValid = false;
                }
            }
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
